/**
 * Phase 16: Lightweight Gmail / Google Calendar / Outlook / Word Online deep-link helpers.
 *
 * No OAuth needed — these just open the official compose / "add event" URLs so the user can
 * complete the action in their preferred mail / calendar / docs app.
 */

const enc = (v) => encodeURIComponent(v ?? "");

/** Format a date for Google Calendar (UTC, basic ISO 8601 without separators) */
const fmtGCal = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
};

/**
 * Build a "Add to Google Calendar" URL.
 * @param {{title:string, description?:string, location?:string, start:Date|string, end?:Date|string}} ev
 */
export const googleCalendarUrl = (ev) => {
  const start = fmtGCal(ev.start);
  const end = fmtGCal(ev.end || new Date(new Date(ev.start).getTime() + 60 * 60 * 1000));
  const dates = `${start}/${end}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title || "Event",
    dates,
    details: ev.description || "",
    location: ev.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Build an "Add to Outlook" URL (Outlook.com / Microsoft 365 web).
 */
export const outlookCalendarUrl = (ev) => {
  const start = ev.start instanceof Date ? ev.start.toISOString() : new Date(ev.start).toISOString();
  const end = ev.end
    ? (ev.end instanceof Date ? ev.end.toISOString() : new Date(ev.end).toISOString())
    : new Date(new Date(ev.start).getTime() + 60 * 60 * 1000).toISOString();
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: ev.title || "Event",
    body: ev.description || "",
    location: ev.location || "",
    startdt: start,
    enddt: end,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Build a Gmail web compose URL with prefilled fields.
 * @param {{to?:string, cc?:string, bcc?:string, subject?:string, body?:string}} m
 */
export const gmailComposeUrl = (m = {}) => {
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: m.to || "",
    su: m.subject || "",
    body: m.body || "",
  });
  if (m.cc) params.set("cc", m.cc);
  if (m.bcc) params.set("bcc", m.bcc);
  return `https://mail.google.com/mail/?${params.toString()}`;
};

/**
 * Build an Outlook Web compose URL with prefilled fields.
 */
export const outlookComposeUrl = (m = {}) => {
  const params = new URLSearchParams({
    path: "/mail/action/compose",
    to: m.to || "",
    subject: m.subject || "",
    body: m.body || "",
  });
  return `https://outlook.live.com/mail/0/deeplink/compose?${params.toString()}`;
};

/**
 * Standard `mailto:` URL for the user's default mail app.
 */
export const mailtoUrl = (m = {}) => {
  const params = new URLSearchParams();
  if (m.subject) params.set("subject", m.subject);
  if (m.body) params.set("body", m.body);
  if (m.cc) params.set("cc", m.cc);
  if (m.bcc) params.set("bcc", m.bcc);
  const qs = params.toString();
  return `mailto:${m.to || ""}${qs ? `?${qs}` : ""}`;
};

/**
 * Build a Google Docs Viewer URL — opens a public file URL inline.
 * Requires a *publicly accessible* file URL (e.g., a /pay/invoice/:id endpoint).
 */
export const googleDocsViewerUrl = (publicFileUrl) =>
  `https://docs.google.com/viewer?url=${enc(publicFileUrl)}&embedded=false`;

/**
 * Build an Office Web Viewer (Word Online) URL for .doc/.docx/.pdf files.
 * Requires a *publicly accessible* file URL.
 */
export const wordOnlineViewerUrl = (publicFileUrl) =>
  `https://view.officeapps.live.com/op/view.aspx?src=${enc(publicFileUrl)}`;
